/** Default suggested-match CIECA filter (ENG-2273): PAS/PASL for sublets, PAA/PAN for parts. */

const SUBLET_CIECA = new Set(["PAS", "PASL"]);
const PARTS_CIECA = new Set(["PAA", "PAN"]);
const PLACEHOLDER_PART_NUMBERS = new Set(["misc", "—", "-", ""]);
const CORE_SUFFIX_MATCH_TAG = "#core";

export function getNexSuggestionCiecaCode(line: { ciecaCode?: string } | null | undefined): string {
    return String(line?.ciecaCode ?? "").trim().toUpperCase();
}

export function hasRealSuggestionPartNumber(partNumber: string | null | undefined): boolean {
    const raw = (partNumber ?? "").trim();
    if (!raw) return false;
    return !PLACEHOLDER_PART_NUMBERS.has(raw.toLowerCase());
}

function normalizeSuggestionPartNumber(partNumber: string): string {
    return partNumber.replace(/[\s-*~]/g, "").toLowerCase();
}

function vendorSkipsCoreSuffixPartEquivalence(vendor: string | null | undefined): boolean {
    const v = (vendor ?? "").toLowerCase();
    return v.includes("tesla") || v.includes("rivian");
}

function partNumberHasDealerCoreSuffix(partNumber: string): boolean {
    const compact = partNumber.replace(/\s/g, "");
    if (/-CORE$/i.test(compact)) return true;
    if (/-C$/i.test(compact)) return true;
    const norm = normalizeSuggestionPartNumber(partNumber);
    return norm.endsWith("core") && norm.length > 4;
}

function coreSuffixStrippedNormalizedPartNumber(partNumber: string): string {
    const norm = normalizeSuggestionPartNumber(partNumber);
    if (norm.endsWith("core") && norm.length > 4) return norm.slice(0, -4);
    const compact = partNumber.replace(/\s/g, "");
    if (/-C$/i.test(compact) && norm.endsWith("c") && norm.length > 1) return norm.slice(0, -1);
    return norm;
}

/** Must stay aligned with API `partNumbersMatchForNexReconciliation`. */
export function suggestionPartNumbersMatchIncludingCoreSuffix(
    invoicePartNumber: string | null | undefined,
    nexPartNumber: string | null | undefined,
    invoiceVendorName?: string | null,
): boolean {
    const a = (invoicePartNumber ?? "").trim();
    const b = (nexPartNumber ?? "").trim();
    if (!a || !b) return false;
    const na = normalizeSuggestionPartNumber(a);
    const nb = normalizeSuggestionPartNumber(b);
    if (na && nb && na === nb) return true;
    if (vendorSkipsCoreSuffixPartEquivalence(invoiceVendorName)) return false;
    if (!partNumberHasDealerCoreSuffix(a) || !partNumberHasDealerCoreSuffix(b)) return false;
    const ka = `${coreSuffixStrippedNormalizedPartNumber(a)}${CORE_SUFFIX_MATCH_TAG}`;
    const kb = `${coreSuffixStrippedNormalizedPartNumber(b)}${CORE_SUFFIX_MATCH_TAG}`;
    return ka.length > CORE_SUFFIX_MATCH_TAG.length && ka === kb;
}

function defaultCiecaCodesForInvoiceType(invoiceType: string | undefined): Set<string> | null {
    if (invoiceType === "sublet") return SUBLET_CIECA;
    // Credited / return-slip POs often include core/return rows with empty or non-PAN/PAA CIECA.
    // Do not hide those behind Show more — only received parts invoices use the OEM/aftermarket default.
    if (invoiceType === "received") return PARTS_CIECA;
    return null;
}

/**
 * Default Flagged Item picker rows: CIECA for the invoice type, plus any NEX line whose
 * part number matches a real invoice part number (eligible regardless of CIECA).
 * Credited and return-slip invoices skip the CIECA hide so core/return rows stay visible.
 * Dealer `-C` / `-CORE` count as the same part except on Tesla and Rivian invoices.
 */
export function nexSuggestionPassesDefaultCiecaFilter(args: {
    invoiceType: string | undefined;
    invoicePartNumber: string | null | undefined;
    nexLine: { ciecaCode?: string; partNumber?: string } | null | undefined;
    invoiceVendorName?: string | null;
}): boolean {
    const allowed = defaultCiecaCodesForInvoiceType(args.invoiceType);
    if (!allowed) return true;
    const cieca = getNexSuggestionCiecaCode(args.nexLine);
    if (cieca && allowed.has(cieca)) return true;
    const invPn = (args.invoicePartNumber ?? "").trim().toLowerCase();
    const nexPn = (args.nexLine?.partNumber ?? "").trim().toLowerCase();
    if (hasRealSuggestionPartNumber(invPn) && hasRealSuggestionPartNumber(nexPn) && invPn === nexPn) {
        return true;
    }
    if (
        hasRealSuggestionPartNumber(args.invoicePartNumber) &&
        hasRealSuggestionPartNumber(args.nexLine?.partNumber) &&
        suggestionPartNumbersMatchIncludingCoreSuffix(args.invoicePartNumber, args.nexLine?.partNumber, args.invoiceVendorName)
    ) {
        return true;
    }
    return false;
}

export function defaultNexSuggestionFilterLabel(invoiceType: string | undefined): string | null {
    if (invoiceType === "sublet") return "Showing sublet (PAS/PASL) lines";
    if (invoiceType === "received") return "Showing OEM/aftermarket (PAN/PAA) lines";
    return null;
}
